import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import useAuthStore from '../store/authStore';
import { 
  getAllBlogs, 
  getBlogById, 
  getCategoryBlogs, 
  getCategoryStats, 
  getPopularBlogs,
  getSimilarBlogs
} from '../api/blogApi';
 
export const useBlogs = () => {
  const { setError } = useAuthStore();
  
  const {
    data: blogs,
    isLoading,
    error,
    refetch
  } = useQuery('blogs', getAllBlogs, {
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to fetch blogs';
      setError(errorMessage);
      toast.error(errorMessage);
    },
    retry: false,
    staleTime: 300000, // 5 minutes
    cacheTime: 3600000, // 1 hour
  });
  
  return {
    blogs,
    isLoading,
    error,
    refetchBlogs: refetch
  };
};

export const useBlog = (blogId) => {
    
  const { setError } = useAuthStore();
  
  const {
    data: blog,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['blog', blogId],
    () => getBlogById(blogId),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch blog details';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000,
      cacheTime: 3600000,
      enabled: blogId !== undefined && blogId !== null,
    }
  );
  
  return {
    blog,
    isLoading,
    error,
    refetchBlog: refetch
  };
};

export const useCategoryBlogs = (category, page = 1, limit = 10) => {
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['categoryBlogs', category, page],
    () => getCategoryBlogs(category, page, limit),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch category blogs';
        toast.error(errorMessage);
      },
      keepPreviousData: true,
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
    }
  );
  
  const {
    data: stats,
    isLoading: statsLoading
  } = useQuery(
    ['categoryStats', category],
    () => getCategoryStats(category),
    {
      staleTime: 300000,
      cacheTime: 3600000,
    }
  );
  
  return {
    blogs: data?.blogs || [],
    pagination: data?.pagination,
    stats,
    isLoading: isLoading || statsLoading,
    error,
    refetchBlogs: refetch
  };
};

export const usePopularBlogs = (limit = 5) => {
  const { setError } = useAuthStore();
  
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['popularBlogs', limit],
    () => getPopularBlogs(limit),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch popular blogs';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
    }
  );
  
  return {
    blogs: data?.blogs || [],
    isLoading,
    error,
    refetchPopularBlogs: refetch
  };
};

export const useSimilarBlogs = (blogId, limit = 5) => {
  const { setError } = useAuthStore();
  
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['similarBlogs', blogId],
    () => getSimilarBlogs(blogId, limit),
    {
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || 'Failed to fetch similar blogs';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000, // 5 minutes
      cacheTime: 3600000, // 1 hour
      enabled: blogId !== undefined && blogId !== null,
    }
  );
  
  return {
    similarBlogs: data?.similarBlogs || [],
    isSameCategory: data?.isSameCategory || false,
    isLoading,
    error,
    refetchSimilarBlogs: refetch
  };
};